import { action, computed, observable, runInAction } from 'mobx';

import { AddonRuntimeInfo } from '../domain/api/addons/AddonRuntimeInfo';

import runtimeContextService from '../services/api/MeetingAddonsDataService';
import aclDataService from '../services/auth/ResourceAccessRuleDataService';

import {
    ChromeState,
    KeyDownMessage,
    ParticipantInfo,
    PredefinedMeetingState,
} from 'meet-addons-sdk';

import PredefinedRoute from '../app/enums/PredefinedRoute';
import { MeetingSchedulingInfo } from '../domain/api/meetings/MeetingSchedulingInfo';
import { PredefinedRole } from '../domain/auth/PredefinedRole';
import { MeetingStateChangedInfo } from '../services/site/MeetingStateChangedInfo';

import navigationStore from './NavigationStore';
import panelStore from './PanelStore';
import { PredefinedHostType } from '../domain/api/addons/PredefinedHostType';
import { ResourceAccessRule } from '../domain/auth/ResourceAccessRule';
import { PredefinedAddonCategory } from '../domain/api/addons/PredefinedAddonCategory';
import { PredefinedResourceType } from '../domain/api/meetings/PredefinedResourceType';
import addonContextDataService from '../services/api/AddonContextDataService';
import meetingDataService from '../services/api/MeetingDataService';
import { isPictureInPictureSupported } from '../utils';
import { AddonInactiveMode } from '../domain/api/addons/AddonInactiveMode';
import { MeetingSnapshot } from '../domain/api/meetings/MeetingSnapshot';
import meetingSnapshotDataService from '../services/api/MeetingSnapshotDataService';
import { MeetingSnapshotUser } from '../domain/site/MeetingSnapshotUser';

const CHROME_STATE_KEY = 'meet-chrome-state';

export interface ILayoutContext {
    tooltipRequested: number;
}

const ONE_MINUTE = 5 * 1000;

export class ContextStore {
    private timer?: number;
    private clockTimer?: number;

    @computed
    public get finishedMeet() {
        switch (this.meetingState) {
            case PredefinedMeetingState.MEETING_COMPLETED:
            case PredefinedMeetingState.MEETING_CANCELED:
            case PredefinedMeetingState.MEETING_ARCHIVED:
            case PredefinedMeetingState.FEEDBACK_COLLECTING:
            case PredefinedMeetingState.FEEDBACK_COMPLETED:
            case PredefinedMeetingState.FEEDBACK_FAILED:
                return true;
            default:
                return false;
        }
    }

    @observable
    public chromeState: ChromeState = ChromeState.FULLSCREEN;

    @observable
    public layout: ILayoutContext = { tooltipRequested: -1 * Date.now() };

    @observable
    public meetingState?: PredefinedMeetingState;

    @observable
    public tenantName!: string;

    @observable
    public tenantLogo!: string;

    @observable
    public resourceId?: string;

    @observable
    public resourceType?: PredefinedResourceType;

    @observable
    public role?: PredefinedRole;

    @observable
    public addonsInfo: AddonRuntimeInfo[] = [];

    @observable
    public participantInfos: ParticipantInfo[] = [];

    @observable
    public totalDurationInSeconds?: number;

    @observable
    public billableDurationInSeconds?: number;

    @observable
    public schedulingInfo?: MeetingSchedulingInfo;

    @observable
    public showFeedbackForm: boolean = false;

    @observable
    public acls: ResourceAccessRule[] = [];

    @observable
    public shellVisible: boolean = true;

    @observable
    public online: boolean = true;

    @observable
    public snapshots: MeetingSnapshot[] = [];

    @observable
    public snapshotUsers: MeetingSnapshotUser[] = [];

    @observable
    public keyDownMessage?: KeyDownMessage;

    @observable
    public loadedSnapshot?: MeetingSnapshot;

    @action
    public setKeyDownMessage = (keyDownMessage: KeyDownMessage) => {
        this.keyDownMessage = keyDownMessage;
    };

    @action
    public setSnapshots = (snapshots: MeetingSnapshot[]) => {
        this.snapshots = snapshots;
    };

    @action
    public addOrUpdateSnapshot = (snapshot: MeetingSnapshot) => {
        var existingSnapshotIndex = this.snapshots.findIndex(
            (s) => s.id === snapshot.id
        );
        if (existingSnapshotIndex === -1) {
            this.snapshots.push(snapshot);
        } else {
            this.snapshots.splice(existingSnapshotIndex, 1, snapshot);
        }
    };

    @action
    public addSnapshotUser = (snapshotUser: MeetingSnapshotUser) => {
        if (!this.snapshotUsers.find((p) => p.userId === snapshotUser.userId)) {
            this.snapshotUsers.push(snapshotUser);
        }
    };

    @action
    public setLoadedSnapshot = (snapshot?: MeetingSnapshot) => {
        this.loadedSnapshot = snapshot;
    };

    @action
    public setOnline = (state: boolean) => {
        if (state === this.online) {
            return;
        }

        this.online = state;
        if (this.online && this.resourceId) {
            // clock it no more then once in a minute
            if (!this.clockTimer) {
                this.clockTimer = window.setTimeout(() => {
                    if (this.online && this.resourceId) {
                        meetingDataService.clockAsync(this.resourceId);
                    }
                    delete this.clockTimer;
                }, ONE_MINUTE);
            }
        }
    };

    @action
    public setAcls = (acls: ResourceAccessRule[]) => {
        this.acls = acls;
    };

    @action
    public setShowFeedbackForm = (visible: boolean) => {
        const feedbackGiven = localStorage.getItem(`fdb-${this.resourceId}`);
        if (feedbackGiven) {
            this.showFeedbackForm = false;
        } else {
            this.showFeedbackForm = visible;
            if (visible) {
                localStorage.setItem(
                    `fdb-${this.resourceId}`,
                    new Date().toUTCString()
                );
            }
        }
    };

    @action
    public setSchedulingInfo = (schedulingInfo: MeetingSchedulingInfo) => {
        this.schedulingInfo = schedulingInfo;
    };

    @action
    public proxyRequestTooltip = (tooltipRequested: number) => {
        this.layout.tooltipRequested = tooltipRequested;
    };

    @action
    public requestTooltip = (highPriority: boolean) => {
        this.layout.tooltipRequested = highPriority
            ? -1 * Date.now()
            : Date.now();
    };

    @action
    public setMeetingStateChangeInfo = (
        changeInfo: MeetingStateChangedInfo
    ) => {
        const newState = changeInfo.state;
        const previousState = this.meetingState;
        this.setMeetingState(changeInfo.state);
        this.setBillableDuration(
            changeInfo.durationInfo.billableDurationInSeconds
        );
        this.setTotalDuration(changeInfo.durationInfo.totalDurationInSeconds);
        this.setSchedulingInfo(changeInfo.schedulingInfo);

        if (!this.timer) {
            this.timer = window.setInterval(() => {
                if (this.online) {
                    if (
                        this.meetingState ===
                            PredefinedMeetingState.MEETING_STARTED ||
                        this.meetingState ===
                            PredefinedMeetingState.BILLABLE_STARTED
                    ) {
                        if (this.totalDurationInSeconds) {
                            this.setTotalDuration(
                                this.totalDurationInSeconds + 1
                            );
                        }
                    }

                    if (
                        this.meetingState ===
                        PredefinedMeetingState.BILLABLE_STARTED
                    ) {
                        if (this.billableDurationInSeconds) {
                            this.setBillableDuration(
                                this.billableDurationInSeconds + 1
                            );
                        }
                    }
                }
            }, 1000);
        }

        if (
            newState === PredefinedMeetingState.MEETING_COMPLETED &&
            newState !== previousState
        ) {
            this.setShowFeedbackForm(true);
            this.initSnapshotsAsync();
        }
    };

    @action
    public setTotalDuration(duration?: number) {
        this.totalDurationInSeconds = duration;
    }

    @action
    public setBillableDuration = (duration?: number) => {
        this.billableDurationInSeconds = duration;
    };

    @action
    public setMeetingState = (state: PredefinedMeetingState) => {
        this.meetingState = state;
    };

    @action
    public setChromeState = (chromeState: ChromeState) => {
        this.chromeState = chromeState;
        localStorage.setItem(CHROME_STATE_KEY, chromeState);
    };

    @action
    public setCurrentResource = (ctx: IResourceContext) => {
        this.resourceId = ctx.rid;
        this.resourceType = ctx.rtype;
        this.role = ctx.role;

        this.tenantName = ctx.tenantName;
        this.tenantLogo = ctx.tenantLogo;
    };

    @action
    public showShell = () => {
        this.shellVisible = true;
    };

    @action
    public hideShell = () => {
        this.shellVisible = false;
    };

    @action
    public initSnapshotsAsync = async () => {
        const meetSnapshotInfos = await meetingSnapshotDataService.getSnapshotsAsync(
            this.resourceId!
        );
        this.setSnapshots(meetSnapshotInfos);
        meetSnapshotInfos.forEach((p) =>
            p.users.forEach((u) => this.addSnapshotUser(u))
        );
        this.setLoadedSnapshot();
    };

    @action
    public removeSnapshot = (snapshot: MeetingSnapshot) => {
        const deletedSnapshot = this.snapshots.find(
            (p) => p.id === snapshot.id
        );
        if (!deletedSnapshot) {
            throw new Error("Can't find snapshot with id:" + snapshot.id);
        }

        const snapshotIndex = this.snapshots.findIndex(
            (p) => p.id === snapshot.id
        );
        if (snapshotIndex > -1) {
            this.snapshots.splice(snapshotIndex, 1);
        }
    };

    @action
    public initAsync = async () => {
        const chromeState =
            (localStorage.getItem(CHROME_STATE_KEY) as ChromeState) ||
            ChromeState.FULLSCREEN;
        this.setChromeState(chromeState);

        if (!this.resourceType || !this.resourceId) {
            throw new Error(
                `Resource information are not set:${this.resourceType}, ${this.resourceId}`
            );
        }

        const meetAcls = await aclDataService.getAccessLicencesAsync(
            this.resourceType,
            this.resourceId
        );
        this.setAcls(meetAcls);

        await this.initSnapshotsAsync();

        const participants = await addonContextDataService.getParticipantsAsync();
        this.addParticipants(participants);

        const addons = await runtimeContextService.getAddonsRuntimeInfoAsync();
        runInAction(() => {
            if (!isPictureInPictureSupported()) {
                // NOTE: nimal, 23.12.2020 - Firefox PiP support is flaky so we will keep manual MINI window appoach
                addons
                    .filter((p) => p.category === PredefinedAddonCategory.CALL)
                    .forEach((addon) => {
                        if (addon.inactiveMode === AddonInactiveMode.HIDDEN) {
                            addon.inactiveMode = AddonInactiveMode.MINI;
                        }
                    });
            }
        });

        this.setAddonRuntimeInfo(addons);

        // register panel addons
        addons
            .filter((p) => p.host.type === PredefinedHostType.PANEL)
            .forEach((addon) =>
                panelStore.register({
                    addonIdentifier: addon.identifier,
                    active: false,
                    exclusive: true,
                    preloaded: addon.preloaded,
                })
            );
    };

    @action
    public addParticipants = (addedParticipantInfos: ParticipantInfo[]) => {
        let added = false;

        if (addedParticipantInfos.length === 0) {
            return;
        }
        addedParticipantInfos.forEach((addedParticipantInfo) => {
            const participantInfoIndex = this.participantInfos.findIndex(
                (p) => p.sessionUserId === addedParticipantInfo.sessionUserId
            );
            if (participantInfoIndex > -1) {
                this.participantInfos.splice(
                    participantInfoIndex,
                    1,
                    addedParticipantInfo
                );
            } else {
                this.participantInfos.push(addedParticipantInfo);
                added = true;
            }
        });
        return added;
    };

    @action
    public removeParticipants = (removeParticipantInfos: ParticipantInfo[]) => {
        const rosterParticipant = removeParticipantInfos.find(
            (p) => p.addonIdentifier === 'roster'
        );
        if (!rosterParticipant) {
            throw new Error(
                'Participant infos do not contain roster addon info'
            );
        }

        removeParticipantInfos.forEach((removeParticipantInfo) => {
            const participantInfoIndex = this.participantInfos.findIndex(
                (p) => p.sessionUserId === removeParticipantInfo.sessionUserId
            );
            if (participantInfoIndex > -1) {
                this.participantInfos.splice(participantInfoIndex, 1);
            }
        });
    };

    @action
    public setAddonRuntimeInfo = (addonsInfo: AddonRuntimeInfo[]) => {
        this.addonsInfo = addonsInfo;
        const callingAddon = this.addonsInfo.find(
            (p) => p.category === PredefinedAddonCategory.CALL
        );
        if (callingAddon) {
            if (!window.location.hash) {
                // note: m1, 13.6.2020 - As there is calling addon we want to focus user on starting the call.
                // TODO: m1, 15.6.2020 - shell hiding commented out until #745 will be implemented
                // this.hideShell();
            }
        }

        this.addonsInfo.forEach((addon) => {
            if (addon.host.type === PredefinedHostType.CANVAS) {
                navigationStore.addRoute({
                    activated: addon.preloaded,
                    exact: true,
                    name: addon.identifier,
                    path: `#/${PredefinedRoute.WORKSPACE}/${addon.identifier}`,
                });
            }
        });
    };
}

export default new ContextStore();
